import React, { useState } from 'react';
import { Button, Spinner, Modal } from 'react-bootstrap';
import { FaFileInvoice, FaCheckCircle, FaClock, FaExclamationTriangle, FaTimesCircle, FaFilePdf, FaSync } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { DteRecord, queryDTEState, QueryDteStateResponse } from '../../../../../../../../../../../../crud/payments_api/dte';
import './DteCard.scss';
import RenderPdf from './components/RenderPdf/RenderPdf';

const DTE_TYPES: { [key: number]: string } = {
  33: 'Factura Electrónica',
  34: 'Factura No Afecta o Exenta',
  39: 'Boleta Electrónica',
  41: 'Boleta Exenta',
  46: 'Factura de Compra',
  52: 'Guía de Despacho',
  56: 'Nota de Débito',
  61: 'Nota de Crédito'
};

interface DteCardProps {
  dte: DteRecord;
  onClick: () => void;
}

const DteCard: React.FC<DteCardProps> = ({ dte, onClick }) => {
  const [showPdfViewer, setShowPdfViewer] = useState<boolean>(false);
  const [checkingStatus, setCheckingStatus] = useState<boolean>(false);
  const [showStatusModal, setShowStatusModal] = useState<boolean>(false);
  const [siiStatus, setSiiStatus] = useState<QueryDteStateResponse | null>(null);

  const getStatusInfo = () => {
    if (dte.failed) {
      return { status: 'failed', label: 'Fallido', icon: <FaTimesCircle />, color: '#f44336' };
    }
    if (dte.sii_rejected) {
      return { status: 'rejected', label: 'Rechazado', icon: <FaExclamationTriangle />, color: '#ff9800' };
    }
    if (dte.sii_accepted) {
      return { status: 'accepted', label: 'Aceptado', icon: <FaCheckCircle />, color: '#4caf50' };
    }
    if (dte.sent_to_sii) {
      return { status: 'sent', label: 'Enviado al SII', icon: <FaClock />, color: '#2196f3' };
    }
    if (dte.sii_envelope_created) {
      return { status: 'envelope', label: 'Sobre Creado', icon: <FaClock />, color: '#9c27b0' };
    }
    if (dte.sii_generated) {
      return { status: 'generated', label: 'Generado', icon: <FaClock />, color: '#607d8b' };
    }
    return { status: 'pending', label: 'Pendiente', icon: <FaClock />, color: '#9e9e9e' };
  };

  const canViewDocument = () => {
    return dte.folio !== null && (dte.sii_accepted || dte.sent_to_sii);
  };

  const canCheckStatus = () => {
    // Can check status if DTE has been sent to SII and has a folio
    return dte.folio !== null && dte.sent_to_sii;
  };

  const handleCheckStatus = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setCheckingStatus(true);
    
    try {
      const response = await queryDTEState(dte.local_id, dte.dte_id);
      setSiiStatus(response.data);
      setShowStatusModal(true);
    } catch (error: any) {
      console.error('Error checking DTE status:', error);
      toast.error('Error al verificar estado en el SII');
    } finally {
      setCheckingStatus(false);
    }
  };

  const getEstadoColor = (estado: string | null | undefined) => {
    if (!estado) return '#9e9e9e'; // Gris - No enviado
    switch (estado) {
      case 'DOK': return '#4caf50'; // Verde - Aceptado
      case 'DNK': return '#ff9800'; // Naranja - Datos no coinciden
      case 'FAU': return '#f44336'; // Rojo - No recibido
      case 'FAN': return '#f44336'; // Rojo - Anulado
      case 'FNA': return '#f44336'; // Rojo - No autorizado
      case 'EMP': return '#f44336'; // Rojo - Empresa no autorizada
      case 'TMD':
      case 'TMC': return '#2196f3'; // Azul - Nota modifica texto
      case 'MMD':
      case 'MMC': return '#9c27b0'; // Púrpura - Nota modifica montos
      case 'AND':
      case 'ANC': return '#f44336'; // Rojo - Nota anula documento
      default: return '#607d8b'; // Gris
    }
  };

  const getEstadoDisplay = (siiStatus: QueryDteStateResponse | null) => {
    if (!siiStatus || !siiStatus.estado) {
      return {
        code: 'N/E',
        glosa: 'No enviado al SII',
        descripcion: 'Este documento aún no ha sido enviado al Servicio de Impuestos Internos.'
      };
    }
    return {
      code: siiStatus.estado,
      glosa: siiStatus.glosaEstado || 'Estado desconocido',
      descripcion: siiStatus.descripcion || 'Sin descripción disponible'
    };
  };

  const handleViewDocument = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowPdfViewer(true);
  };

  const statusInfo = getStatusInfo();
  const dteType = DTE_TYPES[dte.tipo_dte] || `Tipo ${dte.tipo_dte}`;
  const formattedDate = new Date(dte.created_at).toLocaleString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <>
      <div className="dte-card" onClick={onClick}>
        <div className="dte-card-header">
          <div className="dte-type">
            <FaFileInvoice />
            <span>{dteType}</span>
          </div>
          <div className={`dte-status ${statusInfo.status}`} style={{ color: statusInfo.color }}>
            {statusInfo.icon}
            <span>{statusInfo.label}</span>
          </div>
        </div>

        <div className="dte-card-body">
          <div className="dte-info-row">
            <span className="label">Folio:</span>
            <span className="value">{dte.folio}</span>
          </div>
          {dte.track_id && (
            <div className="dte-info-row">
              <span className="label">Track ID:</span>
              <span className="value track-id">{dte.track_id}</span>
            </div>
          )}
          <div className="dte-info-row">
            <span className="label">Creado:</span>
            <span className="value">{formattedDate}</span>
          </div>
        </div>

        {dte.sii_rejection_reason && (
          <div className="dte-card-footer error">
            <span className="error-label">Razón de rechazo:</span>
            <span className="error-message">{dte.sii_rejection_reason}</span>
          </div>
        )}

        {canViewDocument() && (
          <div className="dte-card-footer action">
            <Button 
              variant="primary" 
              size="sm"
              onClick={handleViewDocument}
              className="view-document-btn"
            >
              <FaFilePdf className="me-2" />
              Ver Documento
            </Button>
          </div>
        )}

        {canCheckStatus() && (
          <div className="dte-card-footer action check-status">
            <Button 
              variant="outline-primary" 
              size="sm"
              onClick={handleCheckStatus}
              className="check-status-btn"
              disabled={checkingStatus}
            >
              {checkingStatus ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Verificando...
                </>
              ) : (
                <>
                  <FaSync className="me-2" />
                  Verificar estado SII
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      {dte.folio !== null && (
        <RenderPdf
          show={showPdfViewer}
          onHide={() => setShowPdfViewer(false)}
          dteId={dte.dte_id}
          localId={dte.local_id}
          folio={dte.folio}
          tipoDte={dte.tipo_dte}
        />
      )}

      {/* SII Status Modal */}
      <Modal 
        show={showStatusModal} 
        onHide={() => setShowStatusModal(false)} 
        centered
        className="sii-status-modal"
      >
        <Modal.Header>
          <div className="form-modal-title-container">
            <Modal.Title className="form-modal-title">Estado en el SII</Modal.Title>
          </div>
          <button 
            type="button" 
            className="modal-close-button"
            onClick={() => setShowStatusModal(false)}
            aria-label="Close"
          >
            ×
          </button>
        </Modal.Header>
        <Modal.Body>
          {(() => {
            const estadoDisplay = getEstadoDisplay(siiStatus);
            return (
              <div className="sii-status-content">
                <div 
                  className="status-badge-large"
                  style={{ backgroundColor: getEstadoColor(siiStatus?.estado) }}
                >
                  <span className="estado-code">{estadoDisplay.code}</span>
                  <span className="estado-glosa">{estadoDisplay.glosa}</span>
                </div>
                
                <div className="status-description">
                  <p>{estadoDisplay.descripcion}</p>
                </div>

                {siiStatus?.query && (
                  <div className="status-details">
                    <h6>Información de la consulta</h6>
                    <div className="detail-row">
                      <span className="label">Folio:</span>
                      <span className="value">{siiStatus.query.folio}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Tipo DTE:</span>
                      <span className="value">{DTE_TYPES[siiStatus.query.tipoDte] || siiStatus.query.tipoDte}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Total:</span>
                      <span className="value">${siiStatus.query.total.toLocaleString('es-CL')}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Fecha DTE:</span>
                      <span className="value">{siiStatus.query.fechaDte}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">RUT Emisor:</span>
                      <span className="value">{siiStatus.query.rutEmisor}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">RUT Receptor:</span>
                      <span className="value">{siiStatus.query.rutReceptor}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </Modal.Body>
        <Modal.Footer>
          <div className="form-modal-footer-custom">
            <Button className="form-button-cancel" onClick={() => setShowStatusModal(false)}>
              Cerrar
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DteCard;
